import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Card, CardBody, Typography, Input, Button } from '@material-tailwind/react';
import Select from 'react-select';
import { FaTrash, FaPlus, FaEdit, FaSearch } from 'react-icons/fa';
import { useAuth } from './AuthContext';

const BODY_TYPES = ['Sedan', 'SUV', 'Pickup', 'Van', 'Truck', 'Hatchback', 'Coupe', 'Wagon'];

const { rootUrl } = require('../../config/config');

const VehicleModelsManager = () => {
    const { user } = useAuth();
    const [models, setModels] = useState([]);
    const [makes, setMakes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterMakeId, setFilterMakeId] = useState('');
    const [form, setForm] = useState({
        make_id: '',
        code: '',
        name: '',
        body_type: 'Sedan',
    });

    const makeOptions = useMemo(
        () => makes.map((make) => ({ value: make.id.toString(), label: make.name })),
        [makes]
    );

    const bodyTypeOptions = useMemo(
        () => BODY_TYPES.map((type) => ({ value: type, label: type })),
        []
    );

    const resetForm = () => {
        setEditingId(null);
        setForm({ make_id: '', code: '', name: '', body_type: 'Sedan' });
        setError('');
    };

    const validateForm = () => {
        if (!form.make_id) return 'Make is required';
        if (!form.code.trim()) return 'Model code is required';
        if (!form.name.trim()) return 'Model name is required';
        return '';
    };

    const fetchMakes = async () => {
        try {
            const token = localStorage.getItem('authToken') || 'default-token';
            const res = await axios.get(`${rootUrl}/vehicles/makes`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setMakes(Array.isArray(res.data.data) ? res.data.data : []);
        } catch (err) {
            console.error(err);
            setError('Failed to fetch vehicle makes.');
        }
    };

    const fetchModels = async () => {
        try {
            setLoading(true);
            setError('');
            const token = localStorage.getItem('authToken') || 'default-token';
            const query = filterMakeId ? `?make_id=${filterMakeId}` : '';
            const res = await axios.get(`${rootUrl}/vehicles/models${query}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setModels(Array.isArray(res.data.data) ? res.data.data : []);
        } catch (err) {
            console.error(err);
            setError('Failed to fetch vehicle models.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMakes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        fetchModels();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterMakeId]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationError = validateForm();
        if (validationError) {
            setError(validationError);
            return;
        }

        try {
            const token = localStorage.getItem('authToken') || 'default-token';
            const payload = {
                make_id: parseInt(form.make_id),
                code: form.code.toUpperCase().trim(),
                name: form.name.trim(),
                body_type: form.body_type,
                input_by: user?.username || 'system',
            };

            if (editingId) {
                await axios.put(`${rootUrl}/vehicles/models/${editingId}`, payload, {
                    headers: { Authorization: `Bearer ${token}` },
                });
            } else {
                await axios.post(`${rootUrl}/vehicles/models`, payload, {
                    headers: { Authorization: `Bearer ${token}` },
                });
            }

            resetForm();
            setShowForm(false);
            fetchModels();
        } catch (err) {
            console.error(err);
            const apiData = err?.response?.data;
            let msg = editingId ? 'Failed to update model.' : 'Failed to add model.';
            if (apiData?.message) msg = apiData.message;
            setError(msg);
        }
    };

    const startEdit = async (modelData) => {
        setEditingId(modelData.id);
        setForm({
            make_id: modelData.make_id.toString(),
            code: modelData.code,
            name: modelData.name,
            body_type: modelData.body_type || 'Sedan',
        });
        setShowForm(true);
    };

    const cancelForm = () => {
        resetForm();
        setShowForm(false);
    };

    const deleteModel = async (id) => {
        if (!window.confirm('Delete this vehicle model?')) return;
        try {
            const token = localStorage.getItem('authToken') || 'default-token';
            await axios.delete(`${rootUrl}/vehicles/models/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            fetchModels();
        } catch (err) {
            console.error(err);
            setError('Failed to delete vehicle model.');
        }
    };

    const getMakeName = (makeId) => {
        const make = makes.find((m) => m.id === makeId);
        return make ? make.name : 'Unknown';
    };

    const filteredModels = models.filter(
        (m) =>
            (m.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                m.code.toLowerCase().includes(searchTerm.toLowerCase())) &&
            (!filterMakeId || m.make_id === parseInt(filterMakeId))
    );

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            <Card className="mb-6 shadow-lg">
                <CardBody>
                    <Typography variant="h3" className="mb-4 text-gray-900">
                        Vehicle Models Management
                    </Typography>
                    {error && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-300 text-red-700 rounded">
                            {error}
                        </div>
                    )}
                    {!showForm ? (
                        <Button
                            onClick={() => setShowForm(true)}
                            className="bg-brand-primary text-white shadow-none rounded-none flex items-center gap-2"
                        >
                            <FaPlus /> Add New Model
                        </Button>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-4 p-4 bg-gray-100 rounded">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm text-gray-700 mb-1">Make *</label>
                                    <Select
                                        className="react-select-container"
                                        classNamePrefix="react-select"
                                        placeholder="Select a Make"
                                        options={makeOptions}
                                        value={makeOptions.find((opt) => opt.value === form.make_id) || null}
                                        onChange={(option) => setForm({ ...form, make_id: option?.value || '' })}
                                        isClearable
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-700 mb-1">Code *</label>
                                    <Input
                                        value={form.code}
                                        onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                                        placeholder="HILUX"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-700 mb-1">Name *</label>
                                    <Input
                                        value={form.name}
                                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                                        placeholder="Hilux"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-700 mb-1">Body Type</label>
                                    <Select
                                        className="react-select-container"
                                        classNamePrefix="react-select"
                                        options={bodyTypeOptions}
                                        value={bodyTypeOptions.find((opt) => opt.value === form.body_type) || null}
                                        onChange={(option) => setForm({ ...form, body_type: option?.value || 'Sedan' })}
                                    />
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <Button type="submit" className="bg-green-500 text-white shadow-none rounded-none">
                                    {editingId ? 'Update' : 'Add'}
                                </Button>
                                <Button
                                    type="button"
                                    onClick={cancelForm}
                                    className="bg-gray-500 text-white shadow-none rounded-none"
                                >
                                    Cancel
                                </Button>
                            </div>
                        </form>
                    )}
                    <div className="mt-6 space-y-4">
                        <div className="flex items-center gap-2">
                            <FaSearch className="text-gray-500" />
                            <Input
                                placeholder="Search..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <div>
                            <label className="block text-sm text-gray-700 mb-2">Filter by Make</label>
                            <Select
                                className="react-select-container"
                                classNamePrefix="react-select"
                                placeholder="All Makes"
                                options={makeOptions}
                                value={makeOptions.find((opt) => opt.value === filterMakeId) || null}
                                onChange={(option) => setFilterMakeId(option?.value || '')}
                                isClearable
                            />
                        </div>
                    </div>
                    {loading ? (
                        <Typography className="text-center py-4">Loading...</Typography>
                    ) : filteredModels.length === 0 ? (
                        <Typography className="text-center py-4">No models found</Typography>
                    ) : (
                        <div className="mt-6 overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="bg-gray-200">
                                        <th className="border px-4 py-2">Make</th>
                                        <th className="border px-4 py-2">Code</th>
                                        <th className="border px-4 py-2">Name</th>
                                        <th className="border px-4 py-2">Body Type</th>
                                        <th className="border px-4 py-2">Status</th>
                                        <th className="border px-4 py-2 text-center">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredModels.map((model) => (
                                        <tr key={model.id} className="border hover:bg-gray-50">
                                            <td className="border px-4 py-2 font-semibold">
                                                {getMakeName(model.make_id)}
                                            </td>
                                            <td className="border px-4 py-2">{model.code}</td>
                                            <td className="border px-4 py-2">{model.name}</td>
                                            <td className="border px-4 py-2">{model.body_type || '-'}</td>
                                            <td className="border px-4 py-2">
                                                <span
                                                    className={`px-2 py-1 rounded text-xs ${model.is_active
                                                        ? 'bg-green-100 text-green-700'
                                                        : 'bg-red-100 text-red-700'
                                                        }`}
                                                >
                                                    {model.is_active ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td className="border px-4 py-2 text-center flex gap-2 justify-center">
                                                <Button
                                                    size="sm"
                                                    className="bg-blue-500 text-white"
                                                    onClick={() => startEdit(model)}
                                                >
                                                    <FaEdit />
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    color="red"
                                                    onClick={() => deleteModel(model.id)}
                                                >
                                                    <FaTrash />
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardBody>
            </Card>
        </div>
    );
};

export default VehicleModelsManager;
